import { useState } from 'react'
import {
  useSaveAkuteDemographics,
  type AkuteDemographics,
} from '@pelvia/shared/hooks/patient/useAkute'

/**
 * Collects the demographics Akute needs to create the patient chart (name, sex,
 * DOB, address, phone). Shown when the server returns a 409 `missing_fields` —
 * by both the "send my records" flow and the PelviaCare+ checkout (we collect
 * these *before* charging so we never take money then fail to fulfill).
 */
export default function DemographicsModal({
  missingFields,
  onClose,
  onSaved,
}: {
  missingFields: string[]
  onClose: () => void
  onSaved: () => void
}) {
  const save = useSaveAkuteDemographics()
  const [form, setForm] = useState<AkuteDemographics>({
    first_name: '',
    last_name: '',
    legal_sex: '',
    date_of_birth: '',
    address_line_1: '',
    address_line_2: '',
    address_city: '',
    address_state: '',
    address_zipcode: '',
    primary_phone_number: '',
    primary_phone_type: 'mobile',
  })

  function set<K extends keyof AkuteDemographics>(key: K, value: AkuteDemographics[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const required: (keyof AkuteDemographics)[] = [
    'first_name', 'last_name', 'legal_sex', 'date_of_birth', 'address_line_1',
    'address_city', 'address_state', 'address_zipcode', 'primary_phone_number',
  ]
  const complete = required.every((k) => String(form[k] ?? '').trim())

  function submit() {
    save.mutate(form, { onSuccess: () => onSaved() })
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
        <h3>A few details for your care team</h3>
        <p>
          Your clinician needs these to create your chart. Still needed:{' '}
          {missingFields.join(', ')}.
        </p>
        <div className="demographics-form">
          <input placeholder="First name" value={form.first_name}
                 onChange={(e) => set('first_name', e.target.value)} />
          <input placeholder="Last name" value={form.last_name}
                 onChange={(e) => set('last_name', e.target.value)} />
          <select value={form.legal_sex} onChange={(e) => set('legal_sex', e.target.value)}>
            <option value="">Sex (for medical record)</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
          <input type="date" aria-label="Date of birth" title="Date of birth"
                 max={new Date().toISOString().slice(0, 10)}
                 value={form.date_of_birth}
                 onChange={(e) => set('date_of_birth', e.target.value)} />
          <input placeholder="Phone number" value={form.primary_phone_number}
                 onChange={(e) => set('primary_phone_number', e.target.value)} />
          <input placeholder="Address" value={form.address_line_1}
                 onChange={(e) => set('address_line_1', e.target.value)} />
          <input placeholder="Apt/Suite (optional)" value={form.address_line_2}
                 onChange={(e) => set('address_line_2', e.target.value)} />
          <input placeholder="City" value={form.address_city}
                 onChange={(e) => set('address_city', e.target.value)} />
          <input placeholder="State" value={form.address_state}
                 onChange={(e) => set('address_state', e.target.value)} />
          <input placeholder="ZIP code" value={form.address_zipcode}
                 onChange={(e) => set('address_zipcode', e.target.value)} />
        </div>
        {save.isError && <p className="integration-error">Couldn't save — please try again.</p>}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submit}
            disabled={!complete || save.isPending}
          >
            {save.isPending ? 'Saving…' : 'Save & continue'}
          </button>
        </div>
      </div>
    </div>
  )
}
